import { noop } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { blueLink, grey10, navy } from '@zocdoc/branding/colors';
import greenCheckSvg from './assets/green-check.svg';
import Tooltip from './Tooltip';

export default function ModalBody(props) {
    const { selfPaySettings, sendMetric = noop } = props;

    const {
        initialSessionCost,
        initialSessionLength,
        ongoingSessionLength,
        ongoingSessionMinCost,
        ongoingSessionMaxCost,
        isSlidingScaleOffered,
        helpFileInsuranceClaim,
        chargeInsuranceClaimDifference,
    } = selfPaySettings || {};

    const hasAnyBenefitsInfo =
        initialSessionLength ||
        ongoingSessionLength ||
        isSlidingScaleOffered ||
        helpFileInsuranceClaim ||
        chargeInsuranceClaimDifference;

    const sendViewToolTipMetric = (action, element) => {
        if (sendMetric) {
            sendMetric({
                action: action,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: 'oon modal',
                element: element,
                section: 'oon modal',
            });
        }
    };

    return (
        <StyledWrapper data-test="mh-oon-modal-body-wrapper">
            <InfoParagraph id="mh-oon-modal-description">
                It’s common for mental health providers to be out-of-network.
                Many insurance plans offer out-of-network coverage, so you may
                get partially reimbursed.{' '}
                <a
                    className="semibold nowrap"
                    data-test="mh-oon-modal-learn-more-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://thepapergown.zocdoc.com/your-guide-to-paying-for-therapy/"
                >
                    Learn more
                </a>
            </InfoParagraph>

            {hasAnyBenefitsInfo && (
                <Header2Wrapper>
                    <h2>Out-of-pocket fees and assistance</h2>
                    <Tooltip
                        data-test="mh-oon-modal-section-tooltip"
                        onViewTooltip={() =>
                            sendViewToolTipMetric(
                                'Viewed section tooltip',
                                'section tooltip'
                            )
                        }
                    >
                        Pricing information is provided by the applicable
                        professional and may be subject to change.
                    </Tooltip>
                </Header2Wrapper>
            )}

            {initialSessionLength && (
                <div
                    data-test="mh-oon-modal-initialSession"
                    style={{ marginTop: 16 }}
                >
                    Initial session:{' '}
                    <span className="semibold">
                        ${initialSessionCost} for {initialSessionLength} minutes
                    </span>
                </div>
            )}

            {ongoingSessionLength && (
                <div
                    data-test="mh-oon-modal-ongoingSession"
                    style={{ marginTop: 16 }}
                >
                    Ongoing session:{' '}
                    <span className="semibold">
                        $
                        {ongoingSessionMinCost === ongoingSessionMaxCost
                            ? ongoingSessionMinCost
                            : `${ongoingSessionMinCost}-${ongoingSessionMaxCost}`}{' '}
                        for {ongoingSessionLength} minutes
                    </span>
                </div>
            )}

            <ul>
                {isSlidingScaleOffered && (
                    <li data-test="mh-oon-modal-isSlidingScaleOffered">
                        <span>Sliding scale</span>
                        <Tooltip
                            data-test="mh-oon-modal-scale-tooltip"
                            onViewTooltip={() =>
                                sendViewToolTipMetric(
                                    'Viewed sliding scale tooltip',
                                    'sliding scale tooltip'
                                )
                            }
                        >
                            Offers lower out-of-pocket fees on an individual
                            basis, typically based your ability to pay
                        </Tooltip>
                    </li>
                )}
                {helpFileInsuranceClaim && (
                    <li data-test="mh-oon-modal-helpFileInsuranceClaim">
                        <span>Files your insurance claim</span>
                        <Tooltip
                            data-test="mh-oon-modal-claims-tooltip"
                            onViewTooltip={() =>
                                sendViewToolTipMetric(
                                    'Viewed insurance claims tooltip',
                                    'files insurance claim tooltip'
                                )
                            }
                        >
                            You pay the session fee upfront and the provider
                            files the claim for you, so you can get reimbursed
                            directly without worrying about the paperwork
                        </Tooltip>
                    </li>
                )}
                {chargeInsuranceClaimDifference && (
                    <li data-test="mh-oon-modal-chargeInsuranceClaimDifference">
                        <span>Charges post-reimbursement amount</span>
                        <Tooltip
                            data-test="mh-oon-modal-reimbursement-tooltip"
                            onViewTooltip={() =>
                                sendViewToolTipMetric(
                                    'Viewed insurance reimbursement tooltip',
                                    'insurance reimbursement tooltip'
                                )
                            }
                            shouldShrinkWidth={true}
                        >
                            This provider will only charge you for the amount
                            you pay after you get reimbursed from your insurance
                            plan, so you don’t need to pay the session fee
                            upfront
                        </Tooltip>
                    </li>
                )}
            </ul>
        </StyledWrapper>
    );
}

const StyledWrapper = styled.div`
    color: ${navy};
    font: 14px/22px ${FontFamily.SharpSansMedium};
    ${breakpoint.small} {
        font-size: 16px;
    }

    .bold {
        font-family: ${FontFamily.SharpSansBold};
    }

    .semibold {
        font-family: ${FontFamily.SharpSansSemibold};
    }

    .nowrap {
        white-space: nowrap;
    }

    img {
        border-radius: 50%;
        border: 1px solid ${grey10};
        flex-shrink: 0;
        margin-right: 8px;
        width: 60px;

        ${breakpoint.small} {
            margin-right: 12px;
        }
    }

    h2 {
        display: inline-block;
        font-family: ${FontFamily.SharpSansBold};
    }

    ul {
        margin-left: 22px;
    }

    li {
        margin-top: 16px;
        position: relative;

        ::before {
            background-size: cover;
            background: url(${greenCheckSvg});
            content: '';
            display: block;
            position: absolute;
            left: -20px;
            height: 14px;
            width: 14px;
            top: 3px;
        }

        > * {
            vertical-align: middle;
        }
    }
`;

const Header2Wrapper = styled.div`
    margin-top: 24px;

    > * {
        vertical-align: middle;
    }
`;

const InfoParagraph = styled.p`
    margin-top: 20px;

    a {
        color: ${blueLink};
        text-decoration: underline;
    }
`;
